import { School } from 'distribution/Distribution';

export const USER_REPO = 'USER_REPO';

/**
 * The WordPress maintenance banner's payload. Read as `any` before it moved behind the
 * repo; the shape here is what `App.maintenanceRender` actually reads off it.
 */
export interface MaintenanceStatus {
  maintenance_mode?: boolean;
  maintenance_msg?: string;
  start_time?: string;
  end_time?: string;
}

export interface UserRepo {
  getMaintenanceStatus(): Promise<MaintenanceStatus | false>;
  getMenuData(locale: string): Promise<Array<unknown>>;
  getUserWithToken(code: string): Promise<string | null>;
  /**
   * `undefined` on a failed login: the implementation reports the error as a notification
   * and falls out of its catch without returning. This said `Promise<string>` while the
   * only caller already tested the result for truthiness — the type was the thing that was
   * wrong, not the code.
   */
  getUserWithTokenFromLogPass(email: string, password: string): Promise<string | undefined>;
  logOut(): Promise<void>;
}

export enum UserType {
  Teacher = 'TEACHER',
  Student = 'STUDENT',
  ContentManager = 'CONTENT-MANAGER',
  SuperContentManager = 'SUPER-CM'
}

/** A role string from the server or storage, checked rather than asserted — like isLocale. */
export const isUserType = (value: string | null | undefined): value is UserType =>
  typeof value === 'string' && (Object.values(UserType) as Array<string>).includes(value);

export type UserParams = {
  id: number;
  name: string;
  photo: string;
  schools: Array<School>;
  isSuperCM?: boolean;
  teacherTrial?: boolean;
  isTestAccount?: boolean;
  signature?: string;
};

export abstract class User {
  public readonly type: UserType;
  public readonly name: string;
  public readonly id: number;
  public readonly photo: string;
  public readonly schools: Array<School>;
  public readonly isSuperCM?: boolean;
  public readonly teacherTrial?: boolean;
  public readonly isTestAccount?: boolean;
  public readonly signature?: string;

  protected constructor(params: UserParams & { type: UserType }) {
    this.type = params.type;
    this.id = params.id;
    this.name = params.name;
    this.photo = params.photo;
    this.schools = params.schools;
    this.isSuperCM = params.isSuperCM;
    this.teacherTrial = params.teacherTrial;
    this.isTestAccount = params.isTestAccount;
    this.signature = params.signature;
  }
}
